///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";

import UploadIcon from "../../icons/UploadIcon";
import DownloadIcon from "../../icons/DownloadIcon";
import FileIcon from "../../form-controls/FileIcon";
import {
  downloadBidAttachment,
  toggleDocumentDownloadError,
  openDeleteConfirmationModal,
  resetDeleteItemState,
  deleteDocument,
  fetchBidAttachments
} from "../../../slices/bidSummarySlice";
import PanelStateIndicator from "../PanelStateIndicator";
import DocumentUploadModal from "./DocumentUploadModal";
import Alert from "../../Alert";
import Spinner from "../../icons/Spinner";
import DeleteIcon from "../../icons/DeleteIcon";
import DeleteConfirmationModal from "../DeleteConfirmationModal";
import { deleteItem } from "../../../helpers/types";
import messages from "../../../helpers/messages";
import { formatDateAndTime } from "../../../helpers/formatDateAndTime";

function Attachments({
  loading,
  attachments,
  bidId,
  fetchDetails,
  error,
  documentDownloadError
}) {
  const [uploadDocumentModal, toggleUploadDocumentModal] = useState(false);
  const { modalOpen, item, isLoading, isFailure } = useSelector(
    state => state.bidSummary.deleteItem
  );
  const { user } = useSelector(state => state.user);
  const dispatch = useDispatch();

  const handleDeleteClick = () => {
    dispatch(deleteDocument(bidId, item));
  };

  const closeSystemErrorModal = () => {
    dispatch(resetDeleteItemState());
    dispatch(fetchBidAttachments(bidId));
  };

  return (
    <div className="attachments card pos-relative">
      {uploadDocumentModal && (
        <DocumentUploadModal
          bidId={bidId}
          handleClose={() => toggleUploadDocumentModal(false)}
        />
      )}
      <div
        className="border-bottom-grey card__heading--large pos-relative uppercase widget"
        data-testid="attachment-widget"
      >
        {messages.documents.defaultMessage}
        <button
          data-testid="upload-document-button"
          onClick={() => toggleUploadDocumentModal(true)}
          className="pos-absolute pos-right pos-top mt-12 mr-24 no-focus"
        >
          <UploadIcon width="16px" height="16px" fill="#343434" />
        </button>
      </div>

      {(loading || error) && (
        <div style={{ height: "224px" }} className="p-40 box">
          <PanelStateIndicator
            loading={loading}
            error={error}
            handleRefresh={fetchDetails}
          />
        </div>
      )}

      {attachments?.attachments.length === 0 && (
        <div
          style={{ height: "224px" }}
          className="p-40 box items-center"
          data-testid="no-results-found"
        >
          <p className="uppercase univers-45-light font-size-16 no-results-found">
            {messages.noResultsFound.defaultMessage}
          </p>
        </div>
      )}

      {attachments?.attachments.length > 0 && (
        <div
          style={{ height: "224px" }}
          className="scroll mx-8 mb-2 scroll-on-hover"
        >
          {attachments.attachments.map((attachment, index) => {
            const fileExtension = attachment.fileName.substring(
              attachment.fileName.lastIndexOf(".") + 1
            );
            const lastChild = attachments.attachments.length === index + 1;

            return (
              <div
                className="one-whole grid grid-hover"
                key={index}
                data-testid="attachment"
              >
                <div
                  className={`t100 m-auto pt-6 pb-4 mx-16 ${
                    lastChild ? "" : "border-bottom-grey"
                  }`}
                >
                  <FileIcon
                    className="mx-4 my-6 grid__cell"
                    fileExtension={fileExtension}
                    width="18px"
                    height="22px"
                  />
                  <div className="grid__cell t85 pl-12">
                    <span className="font-bold inline-block pb-2 word-break">
                      {attachment.fileName}
                    </span>
                    <div className="p4 font-size-10 color-grey">
                      <span
                        style={{ maxWidth: "207px" }}
                        className="pr-8 truncate inline-block"
                      >
                        <FormattedMessage
                          {...messages.uploadedByUser}
                          values={{ userName: attachment.uploadByUserName }}
                        />
                      </span>
                      <span className="pl-8 border-left-grey align-top">
                        {formatDateAndTime(
                          attachment.uploadDateUTC,
                          user.division
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="fr">
                    <DeleteIcon
                      height="14px"
                      width="12px"
                      className="mt-12 mr-4 hover-part"
                      onClick={() =>
                        dispatch(
                          openDeleteConfirmationModal({
                            item: attachment.fileName,
                            itemType: deleteItem.document
                          })
                        )
                      }
                    />
                    {attachment.downloadIndicator && (
                      <Spinner
                        className="ml-8 mt-12 spinner"
                        height="12px"
                        width="12px"
                        fill="#343434"
                      />
                    )}
                    {attachment.downloadIndicator || (
                      <DownloadIcon
                        className="ml-8 mt-12 hover-part"
                        width="12px"
                        height="13px"
                        onClick={() =>
                          dispatch(
                            downloadBidAttachment(attachment.fileName, bidId)
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Alert
        message={messages.downloadDocumentErrorMessage.defaultMessage}
        handleClose={() => dispatch(toggleDocumentDownloadError(false))}
        open={documentDownloadError}
      />

      {modalOpen && (
        <DeleteConfirmationModal
          isLoading={isLoading}
          isFailure={isFailure}
          handleClose={() => dispatch(resetDeleteItemState())}
          handleDeleteClick={handleDeleteClick}
          closeSystemErrorModal={closeSystemErrorModal}
          warningMessage={
            messages.deleteDocumentConfirmationMessage.defaultMessage
          }
          title={messages.deleteDocument.defaultMessage}
        />
      )}
    </div>
  );
}

export default Attachments;
